// src/components/assets/AssetsList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Table from '../common/Table';
import Badge from '../common/Badge';
import { assetsService } from '../../services/assets';

const AssetsList = ({ filters = {} }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        setLoading(true);
        const data = await assetsService.getAssets(filters);
        setAssets(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching assets:', err);
        setError('Failed to load assets. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [filters]);

  const columns = [
    {
      header: 'Asset Name',
      accessor: 'asset_name',
      render: (asset) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center">
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {asset.asset_name}
            </div>
            <div className="text-sm text-gray-500">
              ID: {asset.asset_id}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'asset_type',
    },
    {
      header: 'Vendor/Model',
      accessor: 'vendor',
      render: (asset) => (
        <div>
          <div>{asset.vendor || 'N/A'}</div>
          <div className="text-xs text-gray-500">{asset.model_number || ''}</div>
        </div>
      )
    },
    {
      header: 'Location',
      accessor: 'location',
      render: (asset) => asset.location || 'N/A'
    },
    {
      header: 'Criticality',
      accessor: 'criticality_level',
      render: (asset) => <Badge type={asset.criticality_level || 'Low'} />
    },
    {
      header: 'Last Seen',
      accessor: 'last_seen',
      render: (asset) => asset.last_seen ? new Date(asset.last_seen).toLocaleString() : 'Never'
    },
    {
      header: 'Actions',
      render: (asset) => (
        <div className="flex space-x-2">
          <Link
            to={`/assets/${asset.asset_id}`}
            className="text-blue-600 hover:text-blue-900"
          >
            View
          </Link>
          <Link
            to={`/assets/${asset.asset_id}/edit`}
            className="text-green-600 hover:text-green-900"
          >
            Edit
          </Link>
        </div>
      )
    }
  ];

  return (
    <div>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={assets}
        isLoading={loading}
        onRowClick={(asset) => window.location.href = `/assets/${asset.asset_id}`}
        emptyMessage="No assets found. Try adjusting your filters or add a new asset."
      />
    </div>
  );
};

export default AssetsList;
